import React, { useState } from 'react';
import { Search, Filter, X, Clock, Building2, Users, Target } from 'lucide-react';
import { FilterState } from '../types';
import { toLocalDateString, parseLocalDate } from '../utils/dateHelpers';

interface FilterBarProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  totalResults: number;
}

export const FilterBar: React.FC<FilterBarProps> = ({ filters, onFiltersChange, totalResults }) => {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const clearFilters = () => {
    onFiltersChange({
      dateRange: [null, null],
      openPriceRange: [0, Infinity],
      gapPercentRange: [0, Infinity],
      pmVolumeRange: [0, Infinity],
      openToPmhRange: [-Infinity, Infinity],
      fiveMinLowRange: [-Infinity, Infinity],
      thirtyMinLowRange: [-Infinity, Infinity],
      hodTimeRange: ['00:00', '23:59'],
      closeStatus: 'all',
      searchTicker: '',
      marketCapRange: [0, Infinity],
      sharesOutstandingRange: [0, Infinity],
      pmhBreak: 'all', // Nuovo filtro Break PMH
      hodTimeFilter: 'all' // Nuovo filtro Orario HOD
    });
  };

  const formatDateForInput = (date: Date | null) => {
    if (!date) return '';
    return toLocalDateString(date) || '';
  };

  const parseDateFromInput = (dateString: string) => {
    if (!dateString) return null;
    return parseLocalDate(dateString);
  };

  const hasActiveFilters = () => {
    return (
      filters.searchTicker !== '' ||
      filters.closeStatus !== 'all' ||
      filters.pmhBreak !== 'all' || // Includi il nuovo filtro
      filters.hodTimeFilter !== 'all' || // Includi il nuovo filtro Orario HOD
      filters.dateRange[0] !== null ||
      filters.dateRange[1] !== null ||
      filters.openPriceRange[0] !== 0 ||
      filters.openPriceRange[1] !== Infinity ||
      filters.gapPercentRange[0] !== 0 ||
      filters.gapPercentRange[1] !== Infinity ||
      filters.pmVolumeRange[0] !== 0 ||
      filters.pmVolumeRange[1] !== Infinity ||
      filters.openToPmhRange[0] !== -Infinity ||
      filters.openToPmhRange[1] !== Infinity ||
      filters.fiveMinLowRange[0] !== -Infinity ||
      filters.fiveMinLowRange[1] !== Infinity ||
      filters.thirtyMinLowRange[0] !== -Infinity ||
      filters.thirtyMinLowRange[1] !== Infinity ||
      filters.hodTimeRange[0] !== '00:00' ||
      filters.hodTimeRange[1] !== '23:59' ||
      filters.marketCapRange[0] !== 0 ||
      filters.marketCapRange[1] !== Infinity ||
      filters.sharesOutstandingRange[0] !== 0 ||
      filters.sharesOutstandingRange[1] !== Infinity
    );
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-400/10 rounded-lg">
            <Filter className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white">Filtri Dati</h3>
            <p className="text-xs sm:text-sm text-gray-400">
              {totalResults} gap trovati {hasActiveFilters() && '(filtrati)'} • Tutti i gap disponibili
            </p>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
              showAdvancedFilters
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
            }`}
          >
            <Filter className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Avanzati</span>
          </button>
          
          {hasActiveFilters() && (
            <button
              onClick={clearFilters}
              className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors text-sm"
            >
              <X className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Cancella Tutto</span>
              <span className="sm:hidden">Reset</span>
            </button>
          )}
        </div>
      </div>

      {/* Filtri Base */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca ticker..."
            value={filters.searchTicker}
            onChange={(e) => onFiltersChange({ ...filters, searchTicker: e.target.value })}
            className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
          />
        </div>
        
        <select
          value={filters.closeStatus}
          onChange={(e) => onFiltersChange({ ...filters, closeStatus: e.target.value as FilterState['closeStatus'] })}
          className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
        >
          <option value="all">Tutti gli Stati di Chiusura</option>
          <option value="red">Solo Chiusura Rossa</option>
          <option value="green">Solo Chiusura Verde</option>
        </select>

        {/* Nuovo filtro Break PMH */}
        <select
          value={filters.pmhBreak}
          onChange={(e) => onFiltersChange({ ...filters, pmhBreak: e.target.value as FilterState['pmhBreak'] })}
          className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
        >
          <option value="all">Tutti i Break PMH</option>
          <option value="Y">Solo Break PMH (Sì)</option>
          <option value="N">Solo No Break PMH (No)</option>
        </select>

        {/* Nuovo filtro Orario HOD */}
        <select
          value={filters.hodTimeFilter}
          onChange={(e) => onFiltersChange({ ...filters, hodTimeFilter: e.target.value as FilterState['hodTimeFilter'] })}
          className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
        >
          <option value="all">Tutti gli Orari HOD</option>
          <option value="before_1030">HOD &lt; 10:30</option>
          <option value="after_1030">HOD &gt; 10:30</option>
        </select>

        <div className="flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 bg-gray-900/30 rounded-lg border border-gray-600">
          <span className="text-xs sm:text-sm text-gray-300">
            <span className="font-medium text-blue-400">{totalResults}</span> risultati
          </span>
        </div>
      </div>

      {/* Filtri Avanzati */}
      {showAdvancedFilters && (
        <div className="border-t border-gray-600 pt-4 sm:pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {/* Intervallo Date */}
            <div className="space-y-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-300">Intervallo Date</label>
              <div className="space-y-2">
                <input
                  type="date"
                  value={formatDateForInput(filters.dateRange[0])}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    dateRange: [parseDateFromInput(e.target.value), filters.dateRange[1]] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Data inizio"
                />
                <input
                  type="date"
                  value={formatDateForInput(filters.dateRange[1])}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    dateRange: [filters.dateRange[0], parseDateFromInput(e.target.value)] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Data fine"
                />
              </div>
            </div>

            {/* Intervallo Market Cap */}
            <div className="space-y-2">
              <label className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium text-gray-300">
                <Building2 className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>Market Cap ($)</span>
              </label>
              <div className="space-y-2">
                <input
                  type="number"
                  placeholder="Market cap min"
                  value={filters.marketCapRange[0] === 0 ? '' : filters.marketCapRange[0]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    marketCapRange: [Number(e.target.value) || 0, filters.marketCapRange[1]] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <input
                  type="number"
                  placeholder="Market cap max"
                  value={filters.marketCapRange[1] === Infinity ? '' : filters.marketCapRange[1]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    marketCapRange: [filters.marketCapRange[0], Number(e.target.value) || Infinity] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            {/* Intervallo Shares Outstanding */}
            <div className="space-y-2">
              <label className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium text-gray-300">
                <Users className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>Azioni in Circolazione</span>
              </label>
              <div className="space-y-2">
                <input
                  type="number"
                  placeholder="Azioni min"
                  value={filters.sharesOutstandingRange[0] === 0 ? '' : filters.sharesOutstandingRange[0]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    sharesOutstandingRange: [Number(e.target.value) || 0, filters.sharesOutstandingRange[1]] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <input
                  type="number"
                  placeholder="Azioni max"
                  value={filters.sharesOutstandingRange[1] === Infinity ? '' : filters.sharesOutstandingRange[1]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    sharesOutstandingRange: [filters.sharesOutstandingRange[0], Number(e.target.value) || Infinity] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            {/* Intervallo Prezzo Apertura */}
            <div className="space-y-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-300">Prezzo Apertura ($)</label>
              <div className="space-y-2">
                <input
                  type="number"
                  placeholder="Prezzo min"
                  value={filters.openPriceRange[0] === 0 ? '' : filters.openPriceRange[0]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    openPriceRange: [Number(e.target.value) || 0, filters.openPriceRange[1]] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <input
                  type="number"
                  placeholder="Prezzo max"
                  value={filters.openPriceRange[1] === Infinity ? '' : filters.openPriceRange[1]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    openPriceRange: [filters.openPriceRange[0], Number(e.target.value) || Infinity] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            {/* Intervallo Gap % - COMPLETAMENTE MODIFICABILE */}
            <div className="space-y-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-300">Gap % (completamente modificabile)</label>
              <div className="space-y-2">
                <input
                  type="number"
                  placeholder="% min"
                  value={filters.gapPercentRange[0] === 0 ? '' : filters.gapPercentRange[0]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    gapPercentRange: [Number(e.target.value) || 0, filters.gapPercentRange[1]] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <input
                  type="number"
                  placeholder="% max"
                  value={filters.gapPercentRange[1] === Infinity ? '' : filters.gapPercentRange[1]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    gapPercentRange: [filters.gapPercentRange[0], Number(e.target.value) || Infinity] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            {/* Intervallo Volume Pre Market */}
            <div className="space-y-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-300">Volume Pre Market</label>
              <div className="space-y-2">
                <input
                  type="number"
                  placeholder="Volume min"
                  value={filters.pmVolumeRange[0] === 0 ? '' : filters.pmVolumeRange[0]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    pmVolumeRange: [Number(e.target.value) || 0, filters.pmVolumeRange[1]] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <input
                  type="number"
                  placeholder="Volume max"
                  value={filters.pmVolumeRange[1] === Infinity ? '' : filters.pmVolumeRange[1]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    pmVolumeRange: [filters.pmVolumeRange[0], Number(e.target.value) || Infinity] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            {/* Intervallo % Open to PMH */}
            <div className="space-y-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-300">% Apertura a PMH</label>
              <div className="space-y-2">
                <input
                  type="number"
                  placeholder="% min"
                  value={filters.openToPmhRange[0] === -Infinity ? '' : filters.openToPmhRange[0]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    openToPmhRange: [Number(e.target.value) || -Infinity, filters.openToPmhRange[1]] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <input
                  type="number"
                  placeholder="% max"
                  value={filters.openToPmhRange[1] === Infinity ? '' : filters.openToPmhRange[1]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    openToPmhRange: [filters.openToPmhRange[0], Number(e.target.value) || Infinity] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            {/* Intervallo 5 Min Low % */}
            <div className="space-y-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-300">5 Min Low %</label>
              <div className="space-y-2">
                <input
                  type="number"
                  placeholder="% min"
                  value={filters.fiveMinLowRange[0] === -Infinity ? '' : filters.fiveMinLowRange[0]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    fiveMinLowRange: [Number(e.target.value) || -Infinity, filters.fiveMinLowRange[1]] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <input
                  type="number"
                  placeholder="% max"
                  value={filters.fiveMinLowRange[1] === Infinity ? '' : filters.fiveMinLowRange[1]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    fiveMinLowRange: [filters.fiveMinLowRange[0], Number(e.target.value) || Infinity] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            {/* Intervallo 30 Min Low % */}
            <div className="space-y-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-300">30 Min Low %</label>
              <div className="space-y-2">
                <input
                  type="number"
                  placeholder="% min"
                  value={filters.thirtyMinLowRange[0] === -Infinity ? '' : filters.thirtyMinLowRange[0]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    thirtyMinLowRange: [Number(e.target.value) || -Infinity, filters.thirtyMinLowRange[1]] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <input
                  type="number"
                  placeholder="% max"
                  value={filters.thirtyMinLowRange[1] === Infinity ? '' : filters.thirtyMinLowRange[1]}
                  onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    thirtyMinLowRange: [filters.thirtyMinLowRange[0], Number(e.target.value) || Infinity] 
                  })}
                  className="w-full px-2 sm:px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            {/* Intervallo Orario HOD */}
            <div className="space-y-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-300">Intervallo Orario HOD</label>
              <div className="space-y-2">
                <div className="relative">
                  <Clock className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                  <input
                    type="time"
                    value={filters.hodTimeRange[0]}
                    onChange={(e) => onFiltersChange({ 
                      ...filters, 
                      hodTimeRange: [e.target.value, filters.hodTimeRange[1]] 
                    })}
                    className="w-full pl-7 sm:pl-10 pr-2 sm:pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <div className="relative">
                  <Clock className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                  <input
                    type="time"
                    value={filters.hodTimeRange[1]}
                    onChange={(e) => onFiltersChange({ 
                      ...filters, 
                      hodTimeRange: [filters.hodTimeRange[0], e.target.value] 
                    })}
                    className="w-full pl-7 sm:pl-10 pr-2 sm:pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};